/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiPlus, FiX, FiStar } from "react-icons/fi";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import PageContainer from "../components/PageContainer";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";

// Using colors from useUI instead of hardcoded COLORS

export default function Notifications() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);

  // جلب بيانات الـ tenant من localStorage أو من API
  const getTenantData = () => {
    // محاولة جلب من localStorage أولاً
    let tenantName = localStorage.getItem("tenantName") || "";
    // merchantName يجب أن يكون اسم المتجر (clientName, companyName, businessName)
    let merchantName = localStorage.getItem("companyStoreName") ||
      localStorage.getItem("clientName") ||
      localStorage.getItem("companyName") ||
      localStorage.getItem("businessName") ||
      "";

    // إذا لم تكن موجودة، جرب من tenant object
    try {
      const tenantStr = localStorage.getItem("tenant");
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantName = tenantName || tenant.TenantName || tenant.tenantName || "";
        // اسم المتجر يأتي من ClientName, CompanyName, أو BusinessName
        merchantName = merchantName ||
          tenant.ClientName || tenant.clientName ||
          tenant.CompanyName || tenant.companyName ||
          tenant.BusinessName || tenant.businessName ||
          tenant.TenantName || tenant.tenantName || "";
      }
    } catch (e) {
      console.warn("Error parsing tenant from localStorage:", e);
    }

    // إذا لم تكن موجودة، جرب من user object
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        tenantName = tenantName || user.TenantName || user.tenantName || "";
        merchantName = merchantName || user.Username || user.username || "";
      }
    } catch (e) {
      console.warn("Error parsing user from localStorage:", e);
    }

    return { tenantName, merchantName };
  };

  const { tenantName: savedTenantName, merchantName: savedMerchantName } = getTenantData();

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const perms = useTenantStorePermissions();
  const [newNotification, setNewNotification] = useState({
    tenantId: tenantId || 0,
    tenantName: savedTenantName || "",
    merchantName: savedMerchantName || "",
    hasImage: 0,
    imageUrl: "",
    rating: 0,
    description: "",
    isActive: 0,
  });

  // تحديث البيانات المحفوظة عند فتح النموذج
  useEffect(() => {
    if (showModal) {
      const { tenantName, merchantName } = getTenantData();
      setNewNotification(prev => ({
        ...prev,
        tenantId: tenantId || 0,
        tenantName: tenantName || prev.tenantName || "",
        merchantName: merchantName || prev.merchantName || "",
      }));
    } else {
      // Reset image when modal closes
      setSelectedImage(null);
      setImagePreview(null);
    }
  }, [showModal, tenantId]);

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSave = async () => {
    if (!perms.addNotification) {
      return;
    }
    // Validate required fields (لا نحتاج للتحقق من tenantName و merchantName لأنها محفوظة)
    if (!newNotification.description?.trim()) {
      Swal.fire({
        icon: "warning",
        title: isArabic ? "خطأ في التحقق" : "Validation Error",
        text: isArabic ? "يرجى تعبئة الوصف" : "Please fill in the description",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    // التأكد من وجود البيانات المحفوظة
    if (!newNotification.tenantId || !newNotification.tenantName?.trim() || !newNotification.merchantName?.trim()) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: isArabic ? "بيانات المستأجر غير مكتملة. يرجى تسجيل الدخول مرة أخرى" : "Tenant data incomplete. Please login again",
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      // Use proxy in development, full URL in production

      // تنسيق التاريخ (يتم إنشاؤه تلقائياً)
      const formatDate = () => {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
      };

      // Convert image to base64 if selected
      let imageUrl = "";
      if (selectedImage && newNotification.hasImage === 1) {
        const reader = new FileReader();
        imageUrl = await new Promise((resolve, reject) => {
          reader.onloadend = () => {
            // Get base64 string without data:image/... prefix
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(selectedImage);
        });
      }

      // Prepare payload for TenantnNotifications API according to API specification
      const payload = {
        tenantId: newNotification.tenantId || tenantId || 0,
        tenantName: (newNotification.tenantName || "").trim(),
        merchantName: (newNotification.merchantName || "").trim(),
        hasImage: newNotification.hasImage || 0,
        imageUrl: imageUrl || (newNotification.imageUrl || "").trim(),
        rating: newNotification.rating || 0,
        description: (newNotification.description || "").trim(),
        createDate: formatDate(),
        isActive: 0,
      };

      console.log("📤 Sending notification payload:", payload);

      const res = await fetch(`${baseUrl}/TenantnNotifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      // Try to parse response
      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await res.json();
        } catch (parseError) {
          const text = await res.text();
          console.error("Failed to parse JSON response:", parseError);
          console.error("Response text:", text);
          throw new Error("Invalid response format");
        }
      } else {
        const text = await res.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch {
            data = { message: text || `HTTP ${res.status}: ${res.statusText}` };
          }
        }
      }

      if (!res.ok) {
        const errorMessage = data?.message || data?.error || data?.Message || (isArabic
          ? "فشل حفظ الإشعار ❌"
          : "Failed to save notification ❌");

        if (data?.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
            .join("\n");
          console.error("❌ Validation Errors:", validationErrors);
          throw new Error(isArabic
            ? `خطأ في التحقق من البيانات:\n${validationErrors}`
            : `Validation Error:\n${validationErrors}`);
        }

        throw new Error(errorMessage);
      }

      console.log("✅ TenantnNotifications Response:", data);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم الحفظ! ✅" : "Saved! ✅",
        text: data?.message || (isArabic ? "تم حفظ الإشعار بنجاح" : "Notification saved successfully"),
        timer: 1500,
        showConfirmButton: false,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });

      // Reset form (مع الحفاظ على البيانات المحفوظة)
      const { tenantName, merchantName } = getTenantData();
      setNewNotification({
        tenantId: tenantId || 0,
        tenantName: tenantName || "",
        merchantName: merchantName || "",
        hasImage: 0,
        imageUrl: "",
        rating: 0,
        description: "",
      });
      setSelectedImage(null);
      setImagePreview(null);
      setShowModal(false);
    } catch (err) {
      console.error("TenantnNotifications error:", err);

      let errorMessage = err.message || String(err);

      // Handle specific error cases
      if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError")) {
        errorMessage = isArabic
          ? "فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت"
          : "Connection failed. Please check your internet connection";
      } else if (errorMessage.includes("401") || errorMessage.toLowerCase().includes("unauthorized")) {
        errorMessage = isArabic
          ? "غير مصرح. يرجى تسجيل الدخول مرة أخرى"
          : "Unauthorized. Please login again";
      } else if (errorMessage.includes("400")) {
        errorMessage = isArabic
          ? "بيانات غير صحيحة. يرجى التحقق من الحقول المطلوبة"
          : "Invalid data. Please check required fields";
      } else if (errorMessage.includes("500")) {
        errorMessage = isArabic
          ? "خطأ في الخادم. يرجى المحاولة لاحقاً"
          : "Server error. Please try again later";
      }

      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل الحفظ ❌" : "Save Failed ❌",
        text: errorMessage,
        confirmButtonColor: colors.mainColor,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!perms.viewNotification) {
    return <NoAccess />;
  }
  return (
    <PageContainer title={isArabic ? "إضافة رأي/إشعار" : "Add Review/Notification"}>
      <section
        dir={isArabic ? "rtl" : "ltr"}
        className={`min-h-screen px-5 sm:px-10 py-14 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : colors.bg,
        }}
      >
        {/* Add Notification Button */}
        <div className="flex justify-end mb-6">
          {perms.addNotification && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-white font-medium shadow-md transition"
              style={{ background: colors.mainColor }}
            >
              <FiPlus /> {isArabic ? "إضافة رأي/إشعار جديد" : "Add New Review/Notification"}
            </button>
          )}
        </div>

        {/* Info Message */}
        <div
          className={`p-6 rounded-2xl mb-8 ${isDark ? "bg-[#1E293B]/70" : "bg-white/80"
            } border ${isDark ? "border-[#334155]" : "border-gray-200"
            }`}
        >
          <h3 className="text-lg font-semibold mb-2" style={{ color: colors.mainColor }}>
            {isArabic ? "معلومات" : "Information"}
          </h3>
          <p className="text-sm opacity-80">
            {isArabic
              ? "يمكنك إضافة رأي أو إشعار جديد من خلال النقر على الزر أعلاه. سيتم عرض هذه الآراء في الصفحة الرئيسية."
              : "You can add a new review or notification by clicking the button above. These reviews will be displayed on the home page."}
          </p>
        </div>

        {/* Add Notification Modal */}
        {showModal && perms.addNotification && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`w-full max-w-2xl rounded-3xl shadow-2xl p-8 ${isDark ? "bg-[#1E293B]" : "bg-white"
                }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2
                  className="text-2xl font-bold"
                  style={{ color: colors?.mainColor || (isDark ? "#FFD700" : "#6A11CB") }}
                >
                  {isArabic ? "إضافة رأي/إشعار جديد" : "Add New Review/Notification"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg transition"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
                    {isArabic ? "هل تريد إضافة صورة شخصية" : "Do you want to add a profile picture"}
                  </label>
                  <select
                    value={newNotification.hasImage}
                    onChange={(e) => {
                      const hasImage = parseInt(e.target.value);
                      setNewNotification({
                        ...newNotification,
                        hasImage: hasImage,
                      });
                      // Reset image if user selects "No"
                      if (hasImage === 0) {
                        setSelectedImage(null);
                        setImagePreview(null);
                        const fileInput = document.getElementById('image-upload');
                        if (fileInput) {
                          fileInput.value = '';
                        }
                      }
                    }}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark
                        ? "bg-[#0F172A] border-[#334155] text-white"
                        : "bg-white border-gray-300"
                      }`}
                  >
                    <option value={0}>{isArabic ? "لا" : "No"}</option>
                    <option value={1}>{isArabic ? "نعم" : "Yes"}</option>
                  </select>
                </div>

                {/* حقل رفع الصورة - يظهر فقط إذا اختار "نعم" */}
                {newNotification.hasImage === 1 && (
                  <div>
                    <label className="block text-sm font-medium mb-3" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
                      {isArabic ? "رفع الصورة" : "Upload Image"}
                    </label>

                    {/* معاينة الصورة */}
                    {imagePreview ? (
                      <div className="relative group">
                        <div
                          className="relative rounded-xl overflow-hidden border-2"
                          style={{
                            borderColor: isDark ? "#334155" : "#e2e8f0",
                            backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                          }}
                        >
                          {/* Checkerboard background for transparency */}
                          <div
                            className="absolute inset-0 opacity-10"
                            style={{
                              backgroundImage: `
                                linear-gradient(45deg, #ccc 25%, transparent 25%),
                                linear-gradient(-45deg, #ccc 25%, transparent 25%),
                                linear-gradient(45deg, transparent 75%, #ccc 75%),
                                linear-gradient(-45deg, transparent 75%, #ccc 75%)
                              `,
                              backgroundSize: "20px 20px",
                              backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                            }}
                          />
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="relative w-full max-w-md h-64 object-contain mx-auto p-4"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute top-3 right-3 bg-red-500 text-white p-2.5 rounded-full transition-all shadow-lg "
                          style={{ color: "#fff" }}
                        >
                          <FiX size={18} />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="relative border-2 border-dashed rounded-xl p-8 transition-all "
                        style={{
                          borderColor: isDark ? "#475569" : "#cbd5e1",
                          backgroundColor: isDark ? "#0F172A" : "#F8FAFC",
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.borderColor = colors.mainColor;
                          e.currentTarget.style.backgroundColor = isDark ? "#1E293B" : "#f3e8ff";
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.borderColor = isDark ? "#475569" : "#cbd5e1";
                          e.currentTarget.style.backgroundColor = isDark ? "#0F172A" : "#F8FAFC";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.style.borderColor = isDark ? "#475569" : "#cbd5e1";
                          e.currentTarget.style.backgroundColor = isDark ? "#0F172A" : "#F8FAFC";
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith("image/")) {
                            const fakeEvent = { target: { files: [file] } };
                            handleImageChange(fakeEvent);
                          }
                        }}
                      >
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{
                            position: "absolute",
                            width: 0,
                            height: 0,
                            opacity: 0,
                            pointerEvents: "none",
                            visibility: "hidden",
                          }}
                        />
                        <div className="text-center">
                          <div
                            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
                            style={{
                              backgroundColor: isDark ? "rgba(116, 0, 191, 0.2)" : "rgba(116, 0, 191, 0.1)",
                            }}
                          >
                            <svg
                              className="w-8 h-8"
                              style={{ color: colors.mainColor }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <p
                            className="text-sm font-medium mb-2"
                            style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}
                          >
                            {isArabic ? "اسحب الصورة هنا أو" : "Drag image here or"}
                          </p>
                          <label
                            htmlFor="image-upload"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold cursor-pointer transition-all shadow-md"
                            style={{
                              backgroundColor: colors.mainColor,
                              color: "#ffffff",
                            }}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                            {isArabic ? "اختر صورة" : "Choose Image"}
                          </label>
                          <p
                            className="text-xs mt-3"
                            style={{ color: isDark ? "#94a3b8" : "#64748b" }}
                          >
                            {isArabic
                              ? "PNG, JPG, GIF حتى 5MB"
                              : "PNG, JPG, GIF up to 5MB"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-3" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
                    {isArabic ? "التقييم" : "Rating"}
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() =>
                          setNewNotification({
                            ...newNotification,
                            rating: star,
                          })
                        }
                        onMouseEnter={(e) => {
                          // Highlight stars on hover
                          const stars = e.currentTarget.parentElement?.children;
                          if (stars) {
                            Array.from(stars).forEach((s, index) => {
                              if (index < star) {
                                s.style.transform = "scale(1.1)";
                              }
                            });
                          }
                        }}
                        onMouseLeave={(e) => {
                          // Reset stars on mouse leave
                          const stars = e.currentTarget.parentElement?.children;
                          if (stars) {
                            Array.from(stars).forEach((s) => {
                              s.style.transform = "scale(1)";
                            });
                          }
                        }}
                        className="transition-all duration-200 "
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <FiStar
                          size={32}
                          style={{
                            color:
                              star <= newNotification.rating
                                ? colors.gold
                                : isDark
                                  ? "#475569"
                                  : "#cbd5e1",
                            fill:
                              star <= newNotification.rating
                                ? colors.gold
                                : "none",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </button>
                    ))}
                    {newNotification.rating > 0 && (
                      <span
                        className="ml-2 text-sm font-medium"
                        style={{
                          color: isDark ? "#94a3b8" : "#64748b",
                        }}
                      >
                        ({newNotification.rating}/5)
                      </span>
                    )}
                  </div>
                  {newNotification.rating === 0 && (
                    <p
                      className="text-xs mt-2"
                      style={{ color: isDark ? "#94a3b8" : "#64748b" }}
                    >
                      {isArabic
                        ? "انقر على النجوم لتقييم"
                        : "Click on stars to rate"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>
                    {isArabic ? "الوصف" : "Description"} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newNotification.description}
                    onChange={(e) =>
                      setNewNotification({
                        ...newNotification,
                        description: e.target.value,
                      })
                    }
                    placeholder={isArabic ? "أدخل الوصف" : "Enter description"}
                    rows={4}
                    className={`w-full px-4 py-2 rounded-lg border ${isDark
                        ? "bg-[#0F172A] border-[#334155] text-white"
                        : "bg-white border-gray-300"
                      }`}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 rounded-lg border border-gray-300 dark:border-gray-600 transition"
                >
                  {isArabic ? "إلغاء" : "Cancel"}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 rounded-lg text-white font-medium transition"
                  style={{ background: colors.mainColor, opacity: saving ? 0.6 : 1 }}
                >
                  {saving
                    ? isArabic
                      ? "جاري الحفظ..."
                      : "Saving..."
                    : isArabic
                      ? "حفظ"
                      : "Save"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </section>
    </PageContainer>
  );
}

